import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const UnitConverter = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [value, setValue] = useState('100');
  const [fromUnit, setFromUnit] = useState('meter');
  const [toUnit, setToUnit] = useState('centimeter');
  const [category, setCategory] = useState('length');
  const [result, setResult] = useState<number | null>(null);

  const conversionFactors = {
    length: { meter: 1, centimeter: 0.01, kilometer: 1000, millimeter: 0.001, mile: 1609.344, foot: 0.3048, inch: 0.0254, yard: 0.9144 },
    weight: { kilogram: 1, gram: 0.001, milligram: 0.000001, pound: 0.453592, ounce: 0.0283495, ton: 1000, stone: 6.35029 },
    volume: { liter: 1, milliliter: 0.001, gallon: 3.78541, cup: 0.236588, fluid_ounce: 0.0295735, pint: 0.473176, quart: 0.946353 },
    temperature: { celsius: 'C', fahrenheit: 'F', kelvin: 'K' }
  };

  const unitOptions = {
    length: [{ value: 'meter', label: 'Metro (m)' }, { value: 'centimeter', label: 'Centímetro (cm)' }, { value: 'kilometer', label: 'Quilômetro (km)' }, { value: 'mile', label: 'Milha (mi)' }],
    weight: [{ value: 'kilogram', label: 'Quilograma (kg)' }, { value: 'gram', label: 'Grama (g)' }, { value: 'pound', label: 'Libra (lb)' }],
    volume: [{ value: 'liter', label: 'Litro (L)' }, { value: 'milliliter', label: 'Mililitro (mL)' }, { value: 'gallon', label: 'Galão (gal)' }],
    temperature: [{ value: 'celsius', label: 'Celsius (°C)' }, { value: 'fahrenheit', label: 'Fahrenheit (°F)' }, { value: 'kelvin', label: 'Kelvin (K)' }]
  };

  const calculate = () => {
    const val = parseFloat(parseBrazilianNumber(value)) || 0;
    let res: number;

    if (category === 'temperature') {
      res = val;
      if (fromUnit === 'celsius' && toUnit === 'fahrenheit') res = (val * 9 / 5) + 32;
      else if (fromUnit === 'fahrenheit' && toUnit === 'celsius') res = (val - 32) * 5 / 9;
      else if (fromUnit === 'celsius' && toUnit === 'kelvin') res = val + 273.15;
      else if (fromUnit === 'kelvin' && toUnit === 'celsius') res = val - 273.15;
    } else {
      const factors = conversionFactors[category as keyof typeof conversionFactors];
      const fromF = factors[fromUnit as keyof typeof factors] as number;
      const toF = factors[toUnit as keyof typeof factors] as number;
      res = (val * fromF) / toF;
    }

    setResult(res);
    onResultChange([{ label: 'Resultado', value: `${res.toFixed(2)} ${toUnit}` }]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Conversor de Unidades</h2>
        <p className="text-theme-muted mb-4">Converta entre diferentes unidades de medida.</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField label="Categoria" value={category} onChange={setCategory} type="select" options={[{ value: 'length', label: 'Comprimento' }, { value: 'weight', label: 'Peso' }, { value: 'volume', label: 'Volume' }, { value: 'temperature', label: 'Temperatura' }]} required />
          <FormField label="Valor" value={value} onChange={setValue} isNumeric type="text" required />
          <FormField label="De" value={fromUnit} onChange={setFromUnit} type="select" options={unitOptions[category as keyof typeof unitOptions]} required />
          <FormField label="Para" value={toUnit} onChange={setToUnit} type="select" options={unitOptions[category as keyof typeof unitOptions]} required />
        </div>
        <div className="mt-8 flex justify-center">
          <button onClick={calculate} className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:scale-105 transition-all">Converter</button>
        </div>
      </div>

      {result !== null && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ResultCard title="Convertido" value={`${result.toFixed(2)} ${toUnit}`} color="indigo" />
          <ResultCard title="Original" value={`${value} ${fromUnit}`} color="slate" />
        </div>
      )}

      <EducationalBlock title="Dicas" tips={["Sempre confira as unidades.", "0°C = 32°F.", "1kg = 1000g."]} />
    </div>
  );
};

export default UnitConverter;
